import type { BatchStatus, QualityStatus } from "@/features/inventory/types";

/** v1 `tableConfig.jsx` — which last-process stock each stage may consume. */
export type CompatibleProcessRule = {
  lastProcessCode: string;
  qualityFilter?: { enabled: true; status: QualityStatus };
};

export const PROCESS_COMPATIBLE_INVENTORY: Record<string, CompatibleProcessRule[]> = {
  HR_PARTING: [{ lastProcessCode: "PICKLING" }],
  CRM: [
    { lastProcessCode: "HR_PARTING" },
    // v1 "Rework" annealed coil → re-CRM
    { lastProcessCode: "ANNEALING", qualityFilter: { enabled: true, status: "hold" } },
  ],
  ANNEALING: [{ lastProcessCode: "CRM" }],
  SKINPASS: [
    { lastProcessCode: "ANNEALING", qualityFilter: { enabled: true, status: "passed" } },
  ],
  CR_SLITTING: [
    { lastProcessCode: "CRM" },
    { lastProcessCode: "ANNEALING", qualityFilter: { enabled: true, status: "passed" } },
    { lastProcessCode: "SKINPASS" },
  ],
  TUBE: [{ lastProcessCode: "CR_SLITTING" }],
  CTL: [{ lastProcessCode: "TUBE" }],
};

/** v1 selectable: In Stock + Waiting for Production (+ Planned reserved). */
export const PLANNING_SELECTABLE_STATUSES: BatchStatus[] = ["in_stock", "reserved"];

export type PlanningStockBatch = {
  id: string;
  batch_no: string;
  batch_status?: BatchStatus | string;
  quality_status?: QualityStatus | string | null;
  last_process_code?: string | null;
  process_path?: string | null;
  material_name?: string | null;
  grade_name?: string | null;
  location_code?: string | null;
  weight?: number;
  quantity?: number;
  qty?: number;
  thickness?: number | null;
  width?: number | null;
  wt_per_m?: number | null;
  received_at?: string;
  material_id?: string;
  material_grade_id?: string | null;
  length?: number | null;
};

export function isPlanningSelectableBatch(batch: { batch_status?: string | null }): boolean {
  if (!batch.batch_status) return false;
  return PLANNING_SELECTABLE_STATUSES.includes(batch.batch_status as BatchStatus);
}

function matchesRule(
  batch: PlanningStockBatch,
  rule: CompatibleProcessRule,
): boolean {
  const lastCode = (batch.last_process_code || "").toUpperCase();
  const ruleCode = rule.lastProcessCode.toUpperCase();

  if (lastCode !== ruleCode) return false;
  if (rule.qualityFilter?.enabled) {
    return batch.quality_status === rule.qualityFilter.status;
  }
  return true;
}

/** Inbound pickled default — GRN sets last_process = PICKLING when unset. */
function isInboundPickledForParting(batch: PlanningStockBatch): boolean {
  const path = (batch.process_path || "").toUpperCase();
  if (path.includes("PICKLING")) return true;
  if (!batch.last_process_code && !path) return true;
  return false;
}

/**
 * Filter stock for a planning stage (v1 `searchInventory` parity).
 * Returns FIFO-ordered subset when `sort=true` is applied by caller.
 */
export function filterCompatibleInventory<T extends PlanningStockBatch>(
  batches: T[],
  stageProcessCode: string | null | undefined,
): T[] {
  const code = (stageProcessCode || "").toUpperCase();
  if (!code) return batches.filter(isPlanningSelectableBatch);

  const rules = PROCESS_COMPATIBLE_INVENTORY[code];
  if (!rules?.length) return batches.filter(isPlanningSelectableBatch);

  return batches.filter((batch) => {
    if (!isPlanningSelectableBatch(batch)) return false;

    if (code === "HR_PARTING" && isInboundPickledForParting(batch)) {
      return true;
    }

    const lastCode = (batch.last_process_code || "").toUpperCase();
    if (!lastCode) return false;

    return rules.some((rule) => matchesRule(batch, rule));
  });
}

export function compatibleInventoryHint(stageProcessCode: string | null | undefined): string {
  const code = (stageProcessCode || "").toUpperCase();
  const rules = PROCESS_COMPATIBLE_INVENTORY[code];
  if (!rules?.length) return "In stock or reserved batches";
  const parts = rules.map((r) => {
    let s = r.lastProcessCode.replace(/_/g, " ");
    if (r.qualityFilter?.enabled) {
      s += ` (${r.qualityFilter.status})`;
    }
    return s;
  });
  return `Eligible last process: ${parts.join(" · ")}`;
}
